// src/pages/ProfileSettings.jsx
import React from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import countries from "world-countries";

import { PhoneInput } from "@/components/ui/phone-input";

// shadcn/ui
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// icons
import { UserRound, Mail, MapPin, Bell, Trash2, Save, ArrowLeft } from "lucide-react";

const RESIDENCE_COUNTRIES = countries
    .map((c) => ({
        iso2: c.cca2,
        name: c.name?.common ?? c.name?.official ?? c.cca2,
    }))
    .filter((c) => Boolean(c.iso2))
    .sort((a, b) => a.name.localeCompare(b.name));

export default function ProfileSettings() {
    const [form, setForm] = React.useState({
        firstName: "",
        lastName: "",
        email: "",

        phone_e164: "",
        country: "", // ISO2 e.g. "NL"

        city: "",
        addressLine1: "",
        addressLine2: "",
        postalCode: "",
        bio: "",

        language: "en",
        marketingOptIn: false,
    });

    const [saving, setSaving] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [errorMsg, setErrorMsg] = React.useState("");

    // helper for Inputs
    const setField = (key) => (e) => {
        const value = e?.target?.value ?? e;
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    React.useEffect(() => {
        let alive = true;

        (async () => {
            const { data: { session } } = await supabase.auth.getSession();
            const uid = session?.user?.id;
            if (!uid) return;

            const { data: profile, error } = await supabase
                .from("users")
                .select(`
          first_name,last_name,email,
          phone_e164,
          residence_country_iso2,
          country,
          city,
          address_line1,address_line2,postal_code,
          bio,
          language,
          marketing_opt_in
        `)
                .eq("auth_user_id", uid)
                .maybeSingle();

            if (error) throw error;
            if (!alive) return;

            setForm((p) => ({
                ...p,
                firstName: profile?.first_name ?? session.user.user_metadata?.first_name ?? "",
                lastName: profile?.last_name ?? "",
                email: session.user.email ?? profile?.email ?? "",

                phone_e164: profile?.phone_e164 ?? "",
                country: profile?.residence_country_iso2 ?? "",

                city: profile?.city ?? "",
                addressLine1: profile?.address_line1 ?? "",
                addressLine2: profile?.address_line2 ?? "",
                postalCode: profile?.postal_code ?? "",
                bio: profile?.bio ?? "",

                language: profile?.language ?? "en",
                marketingOptIn: profile?.marketing_opt_in ?? false,
            }));
        })()
            .catch((e) => {
                console.error(e);
                if (alive) setErrorMsg("Could not load profile.");
            })
            .finally(() => {
                if (alive) setLoading(false);
            });

        return () => {
            alive = false;
        };
    }, []);

    const [successMsg, setSuccessMsg] = React.useState("");

    const onSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErrorMsg("");
        setSuccessMsg("");

        try {
            const { data: { session } } = await supabase.auth.getSession();
            const uid = session?.user?.id;
            if (!uid) throw new Error("Not signed in");

            const currentEmail = (session.user.email ?? "").toLowerCase();
            const newEmail = (form.email ?? "").trim().toLowerCase();

            const emailChanged = newEmail && newEmail !== currentEmail;

            // Parse phone (optional)
            let phone_country_iso2 = null;
            let phone_calling_code = null;
            let phone_national = null;
            let phone_e164 = null;

            if (form.phone_e164?.trim()) {
                const parsed = parsePhoneNumberFromString(form.phone_e164.trim());
                if (!parsed || !parsed.isValid()) throw new Error("Enter a valid phone number");
                phone_country_iso2 = parsed.country || null;
                phone_calling_code = `+${parsed.countryCallingCode}`;
                phone_national = parsed.nationalNumber;
                phone_e164 = parsed.number;
            }

            const selected = RESIDENCE_COUNTRIES.find((c) => c.iso2 === form.country);
            const country_label = selected?.name ?? null;

            const payload = {
                auth_user_id: uid,

                first_name: form.firstName.trim() || null,
                last_name: form.lastName.trim() || null,

                // store ISO2 here; optionally also store a label in `country` if you want
                residence_country_iso2: form.country || null,
                country: country_label,

                phone_country_iso2,
                phone_calling_code,
                phone_national,
                phone_e164,

                email: (form.email ?? "").trim().toLowerCase() || null,

                city: form.city.trim() || null,
                address_line1: form.addressLine1.trim() || null,
                address_line2: form.addressLine2.trim() || null,
                postal_code: form.postalCode.trim() || null,
                bio: form.bio.trim() || null,

                language: form.language,

                updated_at: new Date().toISOString(),
            };

            const { error } = await supabase
                .from("users")
                .upsert(payload, { onConflict: "auth_user_id" });

            if (error) throw error;

            if (emailChanged) {
                const { error: emailErr } = await supabase.auth.updateUser({
                    email: newEmail,
                });
                if (emailErr) throw emailErr;

                // Optional: show message to user
                setSuccessMsg("Email change requested. Please check your inbox to confirm.");

            }

            // Nice-to-have: keep metadata in sync for quick greetings
            await supabase.auth.updateUser({
                data: { first_name: form.firstName.trim() || null },
            });

            const { error: consentError } = await supabase.rpc(
                "set_member_marketing_consent",
                {
                    p_opt_in: Boolean(form.marketingOptIn),
                    p_locale: form.language || "en",
                    p_source: "profile_settings",
                }
            );

            if (consentError) throw consentError;

            // The database remains the consent source of truth. Surface a sync
            // failure so an opt-out is never presented as fully completed when
            // the email provider could not be reached.
            let marketingSyncFailed = false;

            try {
                const response = await fetch("/api/memberMarketingConsent", {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${session.access_token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({}),
                });

                marketingSyncFailed = !response.ok;
            } catch (syncError) {
                console.error("Marketing preference sync failed:", syncError);
                marketingSyncFailed = true;
            }

            if (marketingSyncFailed) {
                setErrorMsg(
                    "Your profile was saved, but your email preference could not be synced. Please save once more or contact support."
                );
            }

            if (!emailChanged && !marketingSyncFailed) {
                setSuccessMsg("Your profile has been updated.");
            }
        } catch (e) {
            console.error(e);
            setErrorMsg(e?.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="mx-auto w-full max-w-3xl px-4 my-24">
                <p className="font-sans text-sm opacity-70">Loading…</p>
            </div>
        );
    }

    return (
        <div className="mx-auto w-full max-w-3xl px-4 my-24">
            <div className=" md:flex justify-between my-12 px-4">
                <div className="mb-2">
                    <Button asChild variant="ghost" className="rounded-xl px-0 hover:bg-background hover:text-[#565656] ">
                        <Link to="/members">
                            <ArrowLeft className="mr-2 h-4 w-auto" />
                            Back to members
                        </Link>
                    </Button>
                </div>
                <div>
                <h1 className="font-kalice text-4xl font-medium">Profile settings</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                    Update your personal details, preferences, and settings.
                </p>
                {errorMsg ? <p className="mt-3 text-sm text-red-600">{errorMsg}</p> : null}
                {successMsg ? <p className="mt-3 text-sm text-emerald-700">{successMsg}</p> : null}
                </div>
            </div>

            <form onSubmit={onSave} className="grid gap-4">
                {/* Personal */}
                <Card className="rounded-2xl">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <UserRound className="h-5 w-5" />
                            Personal
                        </CardTitle>
                        <CardDescription>How you appear in Antoia.</CardDescription>
                    </CardHeader>

                    <CardContent className="grid gap-4">
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="grid gap-2">
                                <Label htmlFor="firstName">First name</Label>
                                <Input id="firstName" value={form.firstName} onChange={setField("firstName")} className="rounded-xl" />
                            </div>

                            <div className="grid gap-2">
                                <Label htmlFor="lastName">Last name</Label>
                                <Input id="lastName" value={form.lastName} onChange={setField("lastName")} className="rounded-xl" />
                            </div>
                        </div>

                        <div className="grid gap-2 w-full">
                            <Label>Language</Label>
                            <Select value={form.language} onValueChange={(v) => setForm((p) => ({ ...p, language: v }))}>
                                <SelectTrigger className="rounded-xl">
                                    <SelectValue placeholder="Select language" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="en">English</SelectItem>
                                    <SelectItem value="nl">Nederlands</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Contact */}
                <Card className="rounded-2xl">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Mail className="h-5 w-5" />
                            Contact
                        </CardTitle>
                        <CardDescription>Used for secure sign-in, your creations, and support.</CardDescription>
                    </CardHeader>

                    <CardContent className="grid gap-4">
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="grid gap-2 sm:col-span-2">
                                <Label htmlFor="email">Email (login)</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={form.email}
                                    onChange={setField("email")}
                                    className="rounded-xl"
                                />

                            </div>

                            <div className="grid gap-2 sm:col-span-2">
                                <Label htmlFor="phone">Phone (optional)</Label>
                                <PhoneInput
                                    defaultCountry="NL"
                                    international
                                    placeholder="Enter phone number"
                                    value={form.phone_e164}
                                    onChange={(v) => setForm((p) => ({ ...p, phone_e164: v ?? "" }))}
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Address */}
                <Card className="rounded-2xl">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <MapPin className="h-5 w-5" />
                            Address
                        </CardTitle>
                        <CardDescription>For deliveries</CardDescription>
                    </CardHeader>

                    <CardContent className="grid gap-4">
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="grid gap-2 sm:col-span-2">
                                <Label htmlFor="address1">Address line 1</Label>
                                <Input id="address1" value={form.addressLine1} onChange={setField("addressLine1")} className="rounded-xl" />
                            </div>

                            <div className="grid gap-2 sm:col-span-2">
                                <Label htmlFor="address2">Address line 2 (optional)</Label>
                                <Input id="address2" value={form.addressLine2} onChange={setField("addressLine2")} className="rounded-xl" />
                            </div>

                            <div className="grid gap-2">
                                <Label htmlFor="postal">Postal code</Label>
                                <Input id="postal" value={form.postalCode} onChange={setField("postalCode")} className="rounded-xl" />
                            </div>

                            <div className="grid gap-2">
                                <Label htmlFor="city">City</Label>
                                <Input id="city" value={form.city} onChange={setField("city")} className="rounded-xl" />
                            </div>

                            <div className="grid gap-2 sm:col-span-2">
                                <Label htmlFor="country">Country</Label>
                                <Select value={form.country} onValueChange={(v) => setForm((p) => ({ ...p, country: v }))}>
                                    <SelectTrigger className="rounded-xl">
                                        <SelectValue placeholder="Select your country" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {RESIDENCE_COUNTRIES.map((c) => (
                                            <SelectItem key={c.iso2} value={c.iso2}>
                                                {c.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Preferences */}
                <Card className="rounded-2xl">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Bell className="h-5 w-5" />
                            Preferences
                        </CardTitle>
                        <CardDescription>Choose what you want to hear about.</CardDescription>
                    </CardHeader>

                    <CardContent className="grid gap-4">
                        <div className="flex items-start justify-between gap-4 rounded-xl border p-4">
                            <div className="grid gap-1">
                                <p className="font-medium">Essential account emails</p>
                                <p className="text-sm text-muted-foreground">
                                    Secure sign-in links, card delivery, support replies, and important service changes.
                                </p>
                            </div>
                            <span className="rounded-full bg-black px-3 py-1 text-xs text-white">Always on</span>
                        </div>

                        <div className="flex items-start justify-between gap-4 rounded-xl border p-4">
                            <div className="grid gap-1">
                                <p className="font-medium">Antoia invitations and updates</p>
                                <p className="text-sm text-muted-foreground">
                                    Occasional invitations, new experiences, and studio news. Optional — unsubscribe at any time.
                                </p>
                            </div>
                            <Switch
                                checked={form.marketingOptIn}
                                onCheckedChange={(value) =>
                                    setForm((previous) => ({ ...previous, marketingOptIn: value }))
                                }
                                aria-label="Receive Antoia invitations and updates"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Danger zone (UI only for now) */}
                <Card className="rounded-2xl border-destructive/30">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-destructive">
                            <Trash2 className="h-5 w-5" />
                            Danger zone
                        </CardTitle>
                        <CardDescription>Permanent actions for this account.</CardDescription>
                    </CardHeader>
                    <CardContent className="grid gap-4">
                        <div className="rounded-xl border border-destructive/30 p-4">
                            <p className="font-medium">Delete account</p>
                            <p className="mt-1 text-sm text-muted-foreground">
                                This removes your account and personal data experiences. This can’t be undone.
                            </p>
                            <Separator className="my-4" />
                            <Button type="button" variant="destructive" className="rounded-xl">
                                Delete my account
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                    <Button type="button" variant="outline" className="rounded-xl">
                        Cancel
                    </Button>
                    <Button type="submit" className="rounded-xl" disabled={saving}>
                        <Save className="mr-2 h-4 w-4" />
                        {saving ? "Saving…" : "Save changes"}
                    </Button>
                </div>
            </form>
        </div>
    );
}